import { useEffect, useState } from 'preact/hooks';
import { RulesEditor } from '../components/RulesEditor.tsx';
import { ErrorBox, Pill } from '../components/ui.tsx';
import { api, ApiError } from '../lib/api.ts';
import { EVENT_STATUS } from '../lib/labels.ts';
import { byVersionDesc, parseNames } from '../lib/rules.ts';
import type { EventSummary } from '../lib/types.ts';

type Role = 'admin' | 'presidencia' | 'secretaria' | 'legislador' | 'observador';
type Member = { id: string; displayName: string; roles: Role[]; active: boolean; email?: string | null };
type RuleSetSummary = { id: string; version: string; createdAt: string };

const ROLES: Array<[Role, string]> = [
  ['legislador', 'Legislador'], ['presidencia', 'Presidencia'], ['secretaria', 'Secretaría'], ['observador', 'Observador'], ['admin', 'Admin'],
];
const TABS = [['miembros', 'Miembros'], ['sesiones', 'Sesiones'], ['reglas', 'Reglas'], ['organizacion', 'Organización']] as const;
type Tab = (typeof TABS)[number][0];

export function Admin() {
  const [allowed, setAllowed] = useState<boolean | null>(null);
  const tab = (new URLSearchParams(location.search).get('tab') ?? 'miembros') as Tab;
  useEffect(() => {
    api<{ roles: string[] }>('GET', '/api/auth/me').then((me) => setAllowed(me.roles.includes('admin'))).catch((e) => {
      if (e instanceof ApiError && e.status === 401) location.replace('/acceso');
    });
  }, []);
  if (allowed === null) return <div class="app"><p class="hint">Cargando…</p></div>;
  if (!allowed) return <div class="app narrow"><p class="error" role="alert">Esta sección es solo para administradores de la organización.</p><a href="/presidencia">Ir a la conducción de sesiones</a></div>;
  return (
    <div class="app">
      <header class="top">
        <div><h1>Administración</h1><p class="sub">Miembros y accesos, sesiones y reglas de la organización.</p></div>
        <a href="/presidencia">Conducción de sesiones</a>
      </header>
      <nav class="row" aria-label="Secciones" style={{ marginBottom: '16px' }}>
        {TABS.map(([id, label]) => (
          <a key={id} class={`btn ${tab === id ? 'primary' : ''}`} href={`/admin?tab=${id}`} aria-current={tab === id ? 'page' : undefined}>{label}</a>
        ))}
      </nav>
      {tab === 'miembros' && <Members />}
      {tab === 'sesiones' && <Events />}
      {tab === 'reglas' && <RulesEditor />}
      {tab === 'organizacion' && <OrgSettings />}
    </div>
  );
}

function RoleChecks({ value, onChange, idPrefix }: { value: Role[]; onChange: (roles: Role[]) => void; idPrefix: string }) {
  return (
    <div class="row">
      {ROLES.map(([role, label]) => (
        <label key={role} class="check" for={`${idPrefix}-${role}`}>
          <input id={`${idPrefix}-${role}`} type="checkbox" checked={value.includes(role)}
            onChange={(e) => onChange(e.currentTarget.checked ? [...value, role] : value.filter((r) => r !== role))} />
          {label}
        </label>
      ))}
    </div>
  );
}

function Members() {
  const [members, setMembers] = useState<Member[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [name, setName] = useState('');
  const [roles, setRoles] = useState<Role[]>(['legislador']);
  const [bulk, setBulk] = useState('');
  const [editing, setEditing] = useState<string | null>(null);

  const load = () => api<{ members: Member[] }>('GET', '/api/members').then((r) => setMembers(r.members)).catch((e: Error) => setError(e.message));
  useEffect(() => { void load(); }, []);

  const run = async (fn: () => Promise<unknown>, ok?: string) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      if (ok) setNotice(ok);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  const names = parseNames(bulk);
  const legislators = members?.filter((m) => m.active && m.roles.includes('legislador')).length ?? 0;

  return (
    <div class="grid">
      <section class="panel">
        <h2>Agregar</h2>
        <form class="field" onSubmit={(e) => {
          e.preventDefault();
          void run(() => api('POST', '/api/members', { displayName: name, roles }), `${name.trim()} agregado.`).then(() => setName(''));
        }}>
          <label for="nombre">Nombre</label>
          <input id="nombre" type="text" value={name} onInput={(e) => setName(e.currentTarget.value)} required maxLength={120} />
          <RoleChecks value={roles} onChange={setRoles} idPrefix="nuevo" />
          <button class="btn primary" type="submit" disabled={busy || !name.trim() || !roles.length}>Agregar miembro</button>
        </form>
        <form class="block" onSubmit={(e) => {
          e.preventDefault();
          void run(async () => {
            for (const n of names) await api('POST', '/api/members', { displayName: n, roles: ['legislador'] });
          }, `${names.length} legisladores agregados.`).then(() => setBulk(''));
        }}>
          <label for="lista">Varios legisladores a la vez (uno por línea)</label>
          <textarea id="lista" rows={6} value={bulk} onInput={(e) => setBulk(e.currentTarget.value)} placeholder={'Ana López\nBeto Núñez\n…'} />
          <button class="btn" type="submit" disabled={busy || !names.length}>Agregar {names.length || ''} legisladores</button>
        </form>
      </section>

      <section class="panel">
        <div class="row"><h2 style={{ marginRight: 'auto' }}>Miembros</h2><span class="num">{legislators} legisladores activos</span></div>
        <ErrorBox error={error} />
        {notice && <p class="notice" role="status">{notice}</p>}
        <ul class="list">
          {members?.map((m) => (
            <li key={m.id} style={{ flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px' }}>
                <b>{m.displayName}</b>{!m.active && <> <Pill kind="bad">Inactivo</Pill></>}
                <div class="hint">{m.roles.map((r) => ROLES.find(([k]) => k === r)?.[1]).join(', ')}{m.email ? ` · ${m.email}` : ''}</div>
              </div>
              <button class="btn" onClick={() => setEditing(editing === m.id ? null : m.id)} aria-expanded={editing === m.id}>Editar</button>
              {editing === m.id && <MemberEditor member={m} busy={busy} run={run} />}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
}

function MemberEditor({ member, busy, run }: { member: Member; busy: boolean; run: (fn: () => Promise<unknown>, ok?: string) => Promise<void> }) {
  const [roles, setRoles] = useState<Role[]>(member.roles);
  const [email, setEmail] = useState('');
  return (
    <div class="block" style={{ flexBasis: '100%' }}>
      <RoleChecks value={roles} onChange={setRoles} idPrefix={`m-${member.id}`} />
      <div class="row">
        <button class="btn primary" disabled={busy || !roles.length} onClick={() => run(() => api('PATCH', `/api/members/${member.id}`, { roles }), 'Roles actualizados.')}>Guardar roles</button>
        <button class="btn" disabled={busy} onClick={() => run(() => api('PATCH', `/api/members/${member.id}`, { active: !member.active }), member.active ? 'Miembro desactivado.' : 'Miembro activado.')}>
          {member.active ? 'Desactivar' : 'Activar'}
        </button>
      </div>
      {member.email ? (
        <div class="row">
          <span class="hint" style={{ marginRight: 'auto' }}>Entra con {member.email}</span>
          <button class="btn" disabled={busy} onClick={() => run(() => api('DELETE', `/api/members/${member.id}/account`), 'Acceso retirado.')}>Quitar acceso</button>
        </div>
      ) : (
        <form class="row" onSubmit={(e) => { e.preventDefault(); void run(() => api('POST', `/api/members/${member.id}/account`, { email }), `Listo: ${email} ya puede entrar con un enlace por correo.`); }}>
          <input type="email" aria-label={`Email de ${member.displayName}`} placeholder="email para dar acceso" value={email} onInput={(e) => setEmail(e.currentTarget.value)} required style={{ flex: '1 1 200px' }} />
          <button class="btn" type="submit" disabled={busy}>Dar acceso</button>
        </form>
      )}
      <p class="hint">El acceso por email es para presidencia, secretaría, observadores y admins. Los legisladores votan con QR o PIN por sesión.</p>
    </div>
  );
}

function Events() {
  const [events, setEvents] = useState<EventSummary[] | null>(null);
  const [ruleSets, setRuleSets] = useState<RuleSetSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState('');
  const [ruleSetId, setRuleSetId] = useState('');
  const [startsAt, setStartsAt] = useState('');

  const load = async () => {
    try {
      const [e, r] = await Promise.all([
        api<{ events: EventSummary[] }>('GET', '/api/events'),
        api<{ ruleSets: RuleSetSummary[] }>('GET', '/api/rule-sets'),
      ]);
      const sorted = byVersionDesc(r.ruleSets);
      setEvents(e.events);
      setRuleSets(sorted);
      setRuleSetId((id) => id || sorted[0]?.id || '');
    } catch (err) {
      setError((err as Error).message);
    }
  };
  useEffect(() => { void load(); }, []);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setError((e as Error).message);
      if (e instanceof ApiError && e.code === 'stale_version') await load();
    } finally {
      setBusy(false);
    }
  };
  const versionOf = (id: string) => ruleSets.find((r) => r.id === id)?.version ?? '?';

  return (
    <div class="grid">
      <form class="panel" onSubmit={(e) => {
        e.preventDefault();
        void run(() => api('POST', '/api/events', { title, ruleSetId, ...(startsAt ? { startsAt: new Date(startsAt).toISOString() } : {}) })).then(() => setTitle(''));
      }}>
        <h2>Nueva sesión</h2>
        <div class="field"><label for="titulo">Título</label><input id="titulo" type="text" value={title} onInput={(e) => setTitle(e.currentTarget.value)} required maxLength={200} /></div>
        <div class="field">
          <label for="reglas">Reglas</label>
          <select id="reglas" value={ruleSetId} onChange={(e) => setRuleSetId(e.currentTarget.value)} required>
            {ruleSets.map((r) => <option key={r.id} value={r.id}>{r.version}</option>)}
          </select>
        </div>
        <div class="field"><label for="inicio">Fecha y hora (opcional)</label><input id="inicio" type="datetime-local" value={startsAt} onInput={(e) => setStartsAt(e.currentTarget.value)} /></div>
        <button class="btn primary" type="submit" disabled={busy || !title.trim() || !ruleSetId}>Crear sesión</button>
        <p class="hint">Los asuntos se agregan desde la conducción de la sesión.</p>
      </form>
      <section class="panel">
        <h2>Sesiones</h2>
        <ErrorBox error={error} />
        {events?.length === 0 && <p class="hint">Todavía no hay sesiones.</p>}
        <ul class="list">
          {events?.map((e) => (
            <li key={e.id} style={{ flexWrap: 'wrap' }}>
              <div style={{ flex: '1 1 220px' }}>
                <a href={`/presidencia?evento=${e.id}`}><b>{e.title}</b></a>
                <div class="hint">{EVENT_STATUS[e.status]} · reglas {versionOf(e.ruleSetId)}</div>
              </div>
              {e.status !== 'adjourned' && (
                <label class="check">
                  Reglas
                  <select value={e.ruleSetId} disabled={busy} aria-label={`Reglas de ${e.title}`}
                    onChange={(ev) => run(() => api('PATCH', `/api/events/${e.id}`, { version: e.version, ruleSetId: ev.currentTarget.value }))}>
                    {ruleSets.map((r) => <option key={r.id} value={r.id}>{r.version}</option>)}
                  </select>
                </label>
              )}
            </li>
          ))}
        </ul>
        <p class="hint">Cambiar las reglas no afecta una votación abierta: conserva la copia con la que se abrió.</p>
      </section>
    </div>
  );
}

/** Zonas de México primero; después el resto de América y del mundo. */
const MEXICO_ZONES = ['America/Mexico_City', 'America/Monterrey', 'America/Merida', 'America/Cancun', 'America/Chihuahua', 'America/Hermosillo', 'America/Mazatlan', 'America/Tijuana'];

function OrgSettings() {
  const [name, setName] = useState('');
  const [timeZone, setTimeZone] = useState('');
  const [slug, setSlug] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const zones = [...MEXICO_ZONES, ...Intl.supportedValuesOf('timeZone').filter((z) => !MEXICO_ZONES.includes(z))];

  useEffect(() => {
    api<{ org: { name: string; slug: string; timeZone: string } }>('GET', '/api/org').then(({ org }) => {
      setName(org.name);
      setSlug(org.slug);
      setTimeZone(org.timeZone);
    }).catch((e: Error) => setError(e.message));
  }, []);

  const save = async (e: Event) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api('PATCH', '/api/org', { name, timeZone });
      setNotice('Guardado.');
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <form class="panel" style={{ maxWidth: '520px' }} onSubmit={save}>
      <h2>Organización</h2>
      <ErrorBox error={error} />
      {notice && <p class="notice" role="status">{notice}</p>}
      <div class="field"><label for="org-nombre">Nombre</label><input id="org-nombre" type="text" value={name} onInput={(e) => setName(e.currentTarget.value)} required maxLength={120} /></div>
      <div class="field">
        <label for="org-zona">Zona horaria</label>
        <select id="org-zona" value={timeZone} onChange={(e) => setTimeZone(e.currentTarget.value)}>
          {zones.map((z) => <option key={z} value={z}>{z.replace(/_/g, ' ')}</option>)}
        </select>
        <p class="hint">Se usa en las fechas y horas de las actas.</p>
      </div>
      <p class="hint">Dirección: {location.protocol}//{slug}.{location.host.split('.').slice(1).join('.')}</p>
      <button class="btn primary" type="submit" disabled={busy || !name.trim()}>Guardar</button>
    </form>
  );
}
